import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/loader/Loader";

const emptyForm = {
  offer_name: "",
  offer_code: "",
  min_cart_value: "",
  max_cart_value: "",
  discount_type: "PERCENTAGE",
  discount_value: "10",
  priority: "0",
  region_id: "",
  customer_type: "ALL",
  platform: "",
  usage_limit: "",
  per_customer_limit: "",
  active_status: true,
  start_date: "",
  end_date: "",
  reward_type: "",
  reward_name: "",
  product_id: "",
  reward_value: "",
  quantity: "1",
};

export default function CartOffers() {
  const [offers, setOffers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [streamEngineProducts, setStreamEngineProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [offersRes, regionsRes, productsRes] = await Promise.all([
        axios.get("/cartOffers").catch(() => ({ data: { data: [] } })),
        axios.get("/getAllActiveRegionList").catch(() => ({ data: { data: [] } })),
        axios.get("/cartOffers/stream-engine-products").catch(() => ({ data: { data: [] } })),
      ]);

      setOffers(offersRes?.data?.data || []);
      setRegions(regionsRes?.data?.data || []);
      setStreamEngineProducts(productsRes?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOffers = useCallback(async () => {
    try {
      const response = await axios.get("/cartOffers");
      setOffers(response?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load cart offers");
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const updateField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const openNew = () => {
    setSelectedOffer(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (offer) => {
    const reward =
      offer.rewards?.find((item) => item.active_status) ||
      offer.rewards?.[0] ||
      {};

    setSelectedOffer(offer);
    setForm({
      ...emptyForm,
      ...offer,
      min_cart_value: String(offer.min_cart_value ?? ""),
      max_cart_value:
        offer.max_cart_value != null && Number(offer.max_cart_value) > 0
          ? String(offer.max_cart_value)
          : "",
      discount_value: String(offer.discount_value ?? ""),
      priority: String(offer.priority ?? 0),
      region_id: offer.region_id != null ? String(offer.region_id) : "",
      customer_type: offer.customer_type || "ALL",
      platform: offer.platform || "",
      usage_limit: offer.usage_limit != null ? String(offer.usage_limit) : "",
      per_customer_limit:
        offer.per_customer_limit != null
          ? String(offer.per_customer_limit)
          : "",
      start_date: offer.start_date
        ? String(offer.start_date).replace(" ", "T").slice(0, 16)
        : "",
      end_date: offer.end_date
        ? String(offer.end_date).replace(" ", "T").slice(0, 16)
        : "",
      reward_type: reward.reward_type || "",
      reward_name: reward.reward_name || "",
      product_id:
        reward.product_id != null ? String(reward.product_id) : "",
      reward_value:
        reward.reward_value != null ? String(reward.reward_value) : "",
      quantity: String(reward.quantity || 1),
    });
    setOpen(true);
  };

  const saveOffer = async () => {
    if (
      !form.offer_name.trim() ||
      form.min_cart_value === "" ||
      form.discount_value === ""
    ) {
      toast.error("Enter the offer name, minimum cart value, and discount");
      return;
    }
    if (
      form.max_cart_value !== "" &&
      Number(form.max_cart_value) > 0 &&
      Number(form.max_cart_value) < Number(form.min_cart_value)
    ) {
      toast.error("Maximum cart value must be greater than or equal to minimum cart value");
      return;
    }
    if (form.reward_type && !form.reward_name.trim()) {
      toast.error("Enter a reward name");
      return;
    }
    if (form.reward_type === "PRODUCT" && !form.product_id) {
      toast.error("Please select a free product for the reward");
      return;
    }

    const reward = form.reward_type
      ? [
          {
            reward_type: form.reward_type,
            reward_name: form.reward_name.trim(),
            product_id:
              form.reward_type === "PRODUCT"
                ? Number(form.product_id)
                : null,
            reward_value: form.reward_value
              ? Number(form.reward_value)
              : null,
            quantity: Number(form.quantity || 1),
            active_status: true,
          },
        ]
      : [];

    const payload = {
      offer_name: form.offer_name.trim(),
      offer_code: form.offer_code ? form.offer_code.trim() : null,
      min_cart_value: Number(form.min_cart_value),
      max_cart_value:
        form.max_cart_value !== "" && Number(form.max_cart_value) > 0
          ? Number(form.max_cart_value)
          : null,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      priority: Number(form.priority || 0),
      region_id: form.region_id ? Number(form.region_id) : null,
      customer_type: form.customer_type || "ALL",
      platform: form.platform || null,
      usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
      per_customer_limit: form.per_customer_limit
        ? Number(form.per_customer_limit)
        : null,
      active_status: Boolean(form.active_status),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      rewards: reward,
    };

    try {
      setSaving(true);
      if (selectedOffer) {
        await axios.put(`/cartOffers/${selectedOffer.offer_id}`, payload);
        toast.success("Cart offer updated successfully");
      } else {
        await axios.post("/cartOffers", payload);
        toast.success("Cart offer created successfully");
      }
      setOpen(false);
      await loadOffers();
    } catch (error) {
      const errors = error?.response?.data?.errors;
      toast.error(
        errors
          ? Object.values(errors).flat().join(" ")
          : error?.response?.data?.message || "Could not save cart offer"
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (offer) => {
    try {
      await axios.put(`/cartOffers/${offer.offer_id}`, {
        ...offer,
        active_status: !offer.active_status,
        rewards: offer.rewards?.map((reward) => ({
          ...reward,
          product_id: reward.product_id || null,
        })),
      });
      await loadOffers();
      toast.success(
        `Offer ${!offer.active_status ? "activated" : "deactivated"}`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not update offer status"
      );
    }
  };

  const getRegionName = (regionId) => {
    if (!regionId) return "All regions";
    const reg = regions.find((r) => Number(r.region_id) === Number(regionId));
    return reg?.city?.name ? `${reg.city.name}` : `Region #${regionId}`;
  };

  const getRewardDisplay = (offer) => {
    const activeRewards = (offer.rewards || []).filter((r) => r.active_status);
    if (!activeRewards.length) return "None";

    return activeRewards.map((reward) => {
      if (reward.reward_type === "PRODUCT") {
        const prod =
          reward.product ||
          streamEngineProducts.find(
            (p) => Number(p.id) === Number(reward.product_id)
          );
        const name = prod?.product_name || reward.reward_name;
        return `🎁 Free: ${name}`;
      }
      return `${reward.reward_name}${reward.reward_value ? ` (₹${reward.reward_value})` : ""}`;
    }).join(", ");
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      {/* Top Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1E293B">
            Cart Offers & Tier Milestones
          </Typography>
          <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
            Configure shopping cart milestone tiers (e.g. ₹300, ₹500, ₹750, ₹1000) with automatic discounts & free gift rewards.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNew}
          sx={{
            bgcolor: "#FF272F",
            "&:hover": { bgcolor: "#D91C23" },
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            py: 1,
            borderRadius: "8px",
          }}
        >
          Add Cart Offer
        </Button>
      </Stack>

      {/* Main Table */}
      {loading ? (
        <Loader />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Table size="medium">
            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Offer Name & Code
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Cart Value Range (Min - Max)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Discount
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Reward / Free Gift
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Region
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Audience / Platform
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "#475569" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((offer) => (
                <TableRow
                  key={offer.offer_id}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {/* Name & Code */}
                  <TableCell>
                    <Typography fontWeight={600} color="#1E293B" fontSize="14px">
                      {offer.offer_name}
                    </Typography>
                    {offer.offer_code ? (
                      <Chip
                        icon={<LocalOfferIcon sx={{ fontSize: 13 }} />}
                        label={offer.offer_code}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "11px",
                          fontWeight: 700,
                          bgcolor: "#EFF6FF",
                          color: "#1D4ED8",
                          mt: 0.5,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="#94A3B8">
                        Auto-applied at checkout
                      </Typography>
                    )}
                  </TableCell>

                  {/* Min / Max Cart */}
                  <TableCell>
                    <Typography fontWeight={700} color="#0F172A">
                      ₹{Number(offer.min_cart_value).toFixed(2)}
                      {offer.max_cart_value != null && Number(offer.max_cart_value) > 0
                        ? ` – ₹${Number(offer.max_cart_value).toFixed(2)}`
                        : "+"}
                    </Typography>
                    {Number(offer.priority) > 0 && (
                      <Typography variant="caption" color="#64748B">
                        Priority: {offer.priority}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Discount */}
                  <TableCell>
                    <Chip
                      label={
                        offer.discount_type === "PERCENTAGE"
                          ? `${offer.discount_value}% OFF`
                          : `₹${Number(offer.discount_value).toFixed(2)} OFF`
                      }
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#FEF3C7",
                        color: "#92400E",
                      }}
                    />
                  </TableCell>

                  {/* Reward */}
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography fontSize="13px" color="#334155">
                      {getRewardDisplay(offer)}
                    </Typography>
                  </TableCell>

                  {/* Region */}
                  <TableCell>
                    <Chip
                      label={getRegionName(offer.region_id)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "12px", borderColor: "#CBD5E1" }}
                    />
                  </TableCell>

                  {/* Audience / Platform */}
                  <TableCell>
                    <Typography fontSize="12px" color="#475569">
                      {offer.customer_type || "ALL"}
                    </Typography>
                    <Typography fontSize="11px" color="#94A3B8">
                      {offer.platform || "ALL PLATFORMS"}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Switch
                      checked={Boolean(offer.active_status)}
                      onChange={() => toggleStatus(offer)}
                      color="success"
                      size="small"
                    />
                  </TableCell>

                  {/* Edit */}
                  <TableCell align="right">
                    <Tooltip title="Edit Offer">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(offer)}
                        sx={{ color: "#FF272F", "&:hover": { bgcolor: "#FEF2F2" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!offers.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CardGiftcardIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
                    <Typography variant="body1" color="#64748B" fontWeight={500}>
                      No cart offers configured yet.
                    </Typography>
                    <Typography variant="body2" color="#94A3B8" sx={{ mt: 0.5 }}>
                      Click "Add Cart Offer" to create your first milestone tier.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", color: "#1E293B" }}>
          {selectedOffer ? "Edit Cart Offer" : "Create New Cart Offer"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Basic Info */}
            <Typography variant="subtitle2" fontWeight={700} color="#475569">
              Basic Offer Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label="Offer Name"
                  placeholder="e.g. Cart 300 Offer / 1000 Shopping Offer"
                  value={form.offer_name}
                  onChange={updateField("offer_name")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Offer Code (Optional)"
                  placeholder="e.g. CART300 (Blank for auto-apply)"
                  value={form.offer_code || ""}
                  onChange={updateField("offer_code")}
                  helperText="Leave empty to auto-apply when cart meets minimum value"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Min Cart Value (₹)"
                  type="number"
                  inputProps={{ min: 0, step: "1" }}
                  value={form.min_cart_value}
                  onChange={updateField("min_cart_value")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Max Cart Value (₹)"
                  placeholder="Optional (No max)"
                  type="number"
                  inputProps={{ min: 0, step: "1" }}
                  value={form.max_cart_value}
                  onChange={updateField("max_cart_value")}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    label="Discount Type"
                    value={form.discount_type}
                    onChange={updateField("discount_type")}
                  >
                    <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                    <MenuItem value="AMOUNT">Fixed Amount (₹)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={
                    form.discount_type === "PERCENTAGE"
                      ? "Discount (%)"
                      : "Discount (₹)"
                  }
                  type="number"
                  inputProps={{
                    min: 0,
                    step: "0.01",
                    max: form.discount_type === "PERCENTAGE" ? 100 : undefined,
                  }}
                  value={form.discount_value}
                  onChange={updateField("discount_value")}
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            {/* Targeting & Eligibility */}
            <Typography variant="subtitle2" fontWeight={700} color="#475569">
              Targeting & Scope
            </Typography>

            <Grid container spacing={2}>
              {/* Region Select */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Region</InputLabel>
                  <Select
                    label="Region"
                    value={form.region_id}
                    onChange={updateField("region_id")}
                  >
                    <MenuItem value="">All Regions</MenuItem>
                    {regions.map((region) => (
                      <MenuItem
                        key={region.region_id}
                        value={String(region.region_id)}
                      >
                        {region.city?.name
                          ? `${region.city.name} (Region #${region.region_id})`
                          : `Region #${region.region_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Platform */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    label="Platform"
                    value={form.platform}
                    onChange={updateField("platform")}
                  >
                    <MenuItem value="">All Platforms</MenuItem>
                    <MenuItem value="WEB">Web</MenuItem>
                    <MenuItem value="ANDROID">Android</MenuItem>
                    <MenuItem value="IOS">iOS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Customer Type */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    label="Customer Type"
                    value={form.customer_type}
                    onChange={updateField("customer_type")}
                  >
                    <MenuItem value="ALL">All Customers</MenuItem>
                    <MenuItem value="NEW">New Customers Only</MenuItem>
                    <MenuItem value="RETURNING">Returning Customers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Priority Order"
                  type="number"
                  value={form.priority}
                  onChange={updateField("priority")}
                  helperText="Higher priority evaluated first"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Total Usage Limit"
                  type="number"
                  placeholder="Unlimited"
                  value={form.usage_limit}
                  onChange={updateField("usage_limit")}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Per-Customer Limit"
                  type="number"
                  placeholder="Unlimited"
                  value={form.per_customer_limit}
                  onChange={updateField("per_customer_limit")}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={form.start_date}
                  onChange={updateField("start_date")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={form.end_date}
                  onChange={updateField("end_date")}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            {/* Free Reward / Gift Configuration */}
            <Typography variant="subtitle2" fontWeight={700} color="#475569">
              Reward / Free Gift
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Reward Type</InputLabel>
              <Select
                label="Reward Type"
                value={form.reward_type}
                onChange={updateField("reward_type")}
              >
                <MenuItem value="">No Extra Reward (Discount only)</MenuItem>
                <MenuItem value="PRODUCT">Free Product (Stream Engine = 1)</MenuItem>
                <MenuItem value="VOUCHER">Voucher</MenuItem>
                <MenuItem value="WALLET">Wallet Credit</MenuItem>
                <MenuItem value="FREE_DELIVERY">Free Delivery</MenuItem>
                <MenuItem value="COUPON">Special Coupon</MenuItem>
              </Select>
            </FormControl>

            {form.reward_type && (
              <Stack spacing={2} sx={{ bgcolor: "#F8FAFC", p: 2, borderRadius: "8px" }}>
                {form.reward_type === "PRODUCT" && (
                  <FormControl fullWidth required>
                    <InputLabel>Select Free Product (Stream Engine = 1)</InputLabel>
                    <Select
                      label="Select Free Product (Stream Engine = 1)"
                      value={form.product_id}
                      onChange={(e) => {
                        const prodId = e.target.value;
                        const prod = streamEngineProducts.find(
                          (p) => String(p.id) === String(prodId)
                        );
                        setForm((curr) => ({
                          ...curr,
                          product_id: prodId,
                          reward_name: curr.reward_name.trim()
                            ? curr.reward_name
                            : prod?.product_name || "",
                        }));
                      }}
                    >
                      <MenuItem value="">-- Select Stream Engine Product --</MenuItem>
                      {streamEngineProducts.map((prod) => (
                        <MenuItem key={prod.id} value={String(prod.id)}>
                          {prod.product_name} (₹{Number(prod.product_price).toFixed(2)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Reward Display Name"
                      value={form.reward_name}
                      onChange={updateField("reward_name")}
                      required
                      placeholder="e.g. Free Chocolate Pastry"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={form.quantity}
                      onChange={updateField("quantity")}
                      required
                    />
                  </Grid>
                </Grid>

                {form.reward_type !== "PRODUCT" && (
                  <TextField
                    fullWidth
                    label="Reward Value (₹)"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={form.reward_value}
                    onChange={updateField("reward_value")}
                  />
                )}
              </Stack>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.active_status)}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      active_status: event.target.checked,
                    }))
                  }
                  color="success"
                />
              }
              label="Active Status"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#64748B" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveOffer}
            disabled={saving}
            sx={{
              bgcolor: "#FF272F",
              "&:hover": { bgcolor: "#D91C23" },
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {saving ? "Saving..." : selectedOffer ? "Update Offer" : "Create Offer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
